import type { ModelUpdatedEvent, RowDragEvent, RowNode, ValueSetterParams } from "ag-grid-community";
import { uniqBy } from "lodash-es";
import { PureComponent, type ReactElement, createRef } from "react";

import { type ISelectionModel, type TGridSelModelIdentifier } from "../../app-utils/selection-model.js";
import { type InputVocabularyTermInput } from "../../graphql.js";
import { type IEditCellMetadata, getNewPhenotypeColor } from "../../redux/modules/data-editing.js";
import { type IIdentifiable, type ITableColumn } from "../../redux/modules/table-data.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import VocabularyTermManagerControls from "./VocabularyTermManagerControls.js";
import { CELL_TYPE } from "@/settings/settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export interface IInputVocabularyTerm extends InputVocabularyTermInput {
  id?: number | string;
  color?: string;
}

export interface IControlledValuesManagerProps {
  controlledValues: IInputVocabularyTerm[];
  handleControlledValuesChanged: (controlledValues: IInputVocabularyTerm[]) => void;
  editingContext?: boolean;
  label: string;
  maxTermLength: number;
  deleteDisabled?: boolean;
  showColorColumn?: boolean;
}

interface IControlledValuesManagerState {
  itemsSelected?: boolean;
  vocabularyTermColumnDefs?: Array<Partial<ITableColumn>>;
}

class ControlledValuesManager extends PureComponent<IControlledValuesManagerProps, IControlledValuesManagerState> {
  modifyTerm = (params: ValueSetterParams) => {
    const { newValue: label, data } = params;
    const controlledValues: IInputVocabularyTerm[] = this.props.controlledValues.map((c) =>
      // can't use id comparison because this won't work for new terms (not having an ID yet)
      c.name === data.editing.vocabularyTerm.name ? { ...c, name: label } : c,
    );
    this.props.handleControlledValuesChanged(controlledValues);
    return params.newValue !== params.oldValue;
  };

  modifyColor = (params: ValueSetterParams) => {
    const { newValue: color, data } = params;
    const controlledValues: IInputVocabularyTerm[] = this.props.controlledValues.map((c) =>
      // can't use id comparison because this won't work for new terms (not having an ID yet)
      c.name === data.editing.vocabularyTerm.name ? { ...c, color } : c,
    );
    this.props.handleControlledValuesChanged(controlledValues);
    return params.newValue !== params.oldValue;
  };

  private tableInstance = createRef<InMemoryTableWithSelectionModel>();

  constructor(props) {
    super(props);

    const columnDefs: Array<Partial<ITableColumn>> = [
      {
        name: "Value",
        field: "vocabularyTerm.name",
        groupField: "vocabularyTerm",
        cellType: CELL_TYPE.STRING,
        editable: false,
        valueSetter: this.modifyTerm,
      },
    ];
    if (this.props.showColorColumn) {
      columnDefs.push({
        name: "Color",
        field: "vocabularyTerm.color",
        cellType: CELL_TYPE.STRING,
        editable: true,
        valueSetter: this.modifyColor,
      });
    }

    this.state = {
      itemsSelected: false,
      vocabularyTermColumnDefs: columnDefs,
    };
  }

  createRowData = (controlledValues: IInputVocabularyTerm[]): IIdentifiable[] => {
    return controlledValues.map((controlledValue: IInputVocabularyTerm) => {
      const duplicate = controlledValues.filter((term) => term.name.trim() === controlledValue.name.trim()).length > 1;
      const editingMetadata: IEditCellMetadata = duplicate ? { validationError: "Duplicate term" } : {};
      return {
        id: controlledValue.name,
        vocabularyTerm: controlledValue,
        editing: {
          vocabularyTerm: {
            ...controlledValue,
            editingMetadata,
          },
        },
      };
    });
  };

  addNewTerm = (term: string) => {
    const controlledValues = this.props.controlledValues.slice(0);
    controlledValues.push({
      id: null,
      name: term,
      color: this.props.showColorColumn
        ? getNewPhenotypeColor(
            controlledValues.map((c) => c.color),
            term,
          )
        : null,
    });
    this.props.handleControlledValuesChanged(controlledValues);
  };

  handleRemoveSelection = () => {
    const selectedIds: TGridSelModelIdentifier[] = this.tableInstance.current.getSelectedIds();
    const controlledValues: IInputVocabularyTerm[] = this.props.controlledValues.filter(
      (value) => !selectedIds.includes(value.name),
    );
    this.props.handleControlledValuesChanged(controlledValues);
  };

  selectionModelUpdated = (selectionModel: ISelectionModel) => {
    this.setState({ itemsSelected: selectionModel.selectedIds.length > 0 });
  };

  rowsUpdated = (modelUpdated: ModelUpdatedEvent | RowDragEvent) => {
    const controlledValues = [];
    modelUpdated.api.forEachNode((rowNode: RowNode) => {
      controlledValues.push(rowNode.data.vocabularyTerm);
    });
    this.props.handleControlledValuesChanged(controlledValues);
  };

  doesControlledValueExist = (term: string) => {
    return !!this.props.controlledValues.find((existingValue: IInputVocabularyTerm) => existingValue.name === term);
  };

  render(): ReactElement {
    return (
      <FormInputGroup>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <FormLabel>
            {this.props.label} <span className="count">({this.props.controlledValues.length})</span>
          </FormLabel>
          <VocabularyTermManagerControls
            onAddTerm={this.addNewTerm}
            handleRemoveSelection={this.handleRemoveSelection}
            doesControlledValueExist={this.doesControlledValueExist}
            itemsSelected={this.state.itemsSelected}
            parentComponent={this}
            maxTermLength={this.props.maxTermLength}
            deleteDisabled={this.props.deleteDisabled}
          />
        </div>

        <div style={{ maxWidth: "unset" }}>
          <InMemoryTableWithSelectionModel
            columns={this.state.vocabularyTermColumnDefs}
            ref={this.tableInstance}
            rowGridData={this.createRowData(this.props.controlledValues)}
            uniqueGridIdentifier={"controlled-values"}
            useSelectionModel={!this.props.deleteDisabled}
            allowRowDragging={true}
            noRowsMessage={"Add some vocabulary terms"}
            selectionModelUpdated={this.selectionModelUpdated}
            gridProps={{
              onRowDragEnd: this.rowsUpdated,
              domLayout: "autoHeight",
              enableFillHandle: false,
              stopEditingWhenCellsLoseFocus: false,
            }}
            gridContext={{ editMode: true }}
          />
        </div>
      </FormInputGroup>
    );
  }
}

export function checkUniqueTerms(terms: IInputVocabularyTerm[]): boolean {
  return uniqBy(terms, "name").length === terms.length;
}

export default ControlledValuesManager;
